import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'localization';
import AtlasTree from '../../molecules/Atlas/AtlasTree/AtlasTree';
import AtlasTreeMobile from '../../molecules/Atlas/AtlasTreeMobile/AtlasTreeMobile';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { useSelector } from 'react-redux';
import { selectSphereId } from '@center-pwa/selectors/selectors';
import './AtlasComponent.scss';
import { useMediaQuery } from 'design-system/my-lib';
import { AtlasViewContext, getResponsiveBreakpoint } from './AtlasViewContext';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useContextView } from '../../../hooks/useContextView';
import { useContextViewPermissions, resetContextViewPermissionsCache } from '../../../hooks/useContextViewPermissions';
import { useContextSelection } from '../../../hooks/useContextSelection';
import { getContext, getContextViewsByContext } from '../../../api/contextApi';
import type { ContextViewRecord } from '../../../api/contextApi';
import ContextSwitcherToolbarMolecule from '../../molecules/ContextSwitcherToolbar/ContextSwitcherToolbarMolecule';
import { globalSphereCache } from '@center-platform/spheres/cache';
import ContextViewEditorOverlayOrganism from '../ContextViewEditorOverlay/ContextViewEditorOverlayOrganism';
import { ATLAS_DEFAULT_VIEW_CONFIG, mergeAtlasViewConfig } from './defaults';
import { useLevelFilters, VIEW_WIDE_SCOPE, type FilterSaveTarget } from '../../../hooks/useLevelFilters';
import { useArcTemplates } from '../../../hooks/useArcTemplates';
import { useAspectCatalog } from '../../../hooks/useAspectCatalog';
import type { SchemaForFilters } from '../../../utils/filterPropertyBuilder';
import type { AtlasViewConfig } from 'types/contextView';
import type { Context } from 'types/context';
import type { UnifiedFilters } from 'types/filters';

interface AtlasComponentProps {
  /** Component default context system name (the metaspace's pinned context). */
  contextSystemName: string;
  /** Optional ContextView system name for view-specific node composition. */
  viewSystemName?: string;
  sphereId?: string;
  /** Organization for context lookup / switcher listing. */
  organizationId: string;
  /** Sphere type that drives switcher applicability filtering (null = no filter). */
  sphereType?: string | null;
  onChatBubbleClick?: (sphereId: string) => void;
  /** Whether to show the context switcher toolbar above the tree. */
  showToolbar?: boolean;
  /** Navigate to start a brand-new context (from the switcher). */
  onNewContext?: () => void;
  /** Navigate to the context management page (from the switcher). */
  onManageContexts?: () => void;
  /** Navigate to the context editor for the given context system name. */
  onEditContext?: (contextSystemName: string) => void;
}

const AtlasComponent: React.FC<AtlasComponentProps> = React.memo(
  ({
    contextSystemName,
    viewSystemName,
    sphereId: propSphereId,
    organizationId,
    sphereType = null,
    onChatBubbleClick,
    showToolbar = true,
    onNewContext,
    onManageContexts,
    onEditContext,
  }) => {
    const { t, i18n } = useTranslation();
    const apiClients = useApiClientsOrNull();
    const toastRef = useRef<Toast>(null);
    const reduxSphereId = useSelector(selectSphereId);
    const sphereId = propSphereId || reduxSphereId;
    const isMobile = useMediaQuery('(max-width: 768px)');

    const [rootSphereName, setRootSphereName] = useState<string | undefined>();
    useEffect(() => {
      if (!sphereId || !apiClients?.graphClient) return;
      globalSphereCache.getSphereData(sphereId, apiClients.graphClient).then((data) => {
        const name = data.info.sphere?.properties?.name ?? data.info.properties?.name;
        if (name) setRootSphereName(name);
      }).catch(() => { /* display-name only; fall back gracefully */ });
    }, [sphereId, apiClients]);

    const [containerWidth, setContainerWidth] = useState<number>(0);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);

    const pageLimit = 15;

    // ── Context selection (localStorage-scoped per sphere; the metaspace's
    // contextSystemName is the default the user can switch away from) ──────────
    const { activeContextSystemName, setActiveContextSystemName } = useContextSelection({
      sphereId: sphereId ?? '',
      scopeKey: 'atlas',
      defaultContextSystemName: contextSystemName,
    });

    // Load the active context purely for its display name (the tree fetches its
    // own data via useContextTree).
    const [contextObj, setContextObj] = useState<Context | null>(null);
    useEffect(() => {
      setContextObj(null);
      if (!apiClients?.schemaClient || !activeContextSystemName) return;
      let cancelled = false;
      getContext(apiClients.schemaClient, activeContextSystemName)
        .then((result) => {
          if (!cancelled) setContextObj(result);
        })
        .catch(() => {
          /* display-name only; fall back to system name */
        });
      return () => {
        cancelled = true;
      };
    }, [apiClients, activeContextSystemName]);

    const activeContextDisplayName = useMemo(() => {
      if (!contextObj) return activeContextSystemName;
      const lang = i18n.language;
      return (
        contextObj.name?.translations?.[lang] || contextObj.name?.default || activeContextSystemName
      );
    }, [contextObj, activeContextSystemName, i18n.language]);

    // ── Active ContextView (runtime-switchable; seeded from the viewSystemName prop) ─
    const [activeViewSystemName, setActiveViewSystemName] = useState<string | null>(
      viewSystemName ?? null
    );
    useEffect(() => {
      setActiveViewSystemName(viewSystemName ?? null);
    }, [viewSystemName]);

    const { view: fetchedView } = useContextView(apiClients, activeViewSystemName ?? undefined);
    // Holds the record `useLevelFilters` wrote (via `onViewUpdated`) between a
    // save and the next `activeViewSystemName` fetch settling — otherwise a
    // second column-filter save in the same session would read the stale
    // pre-save `base_config` `useContextView` still has cached.
    const [viewOverride, setViewOverride] = useState<ContextViewRecord | null>(null);
    useEffect(() => {
      setViewOverride(null);
    }, [activeViewSystemName]);
    const view = viewOverride ?? fetchedView;

    const { canEdit: canEditActiveView, loading: canEditActiveViewLoading } = useContextViewPermissions(
      apiClients,
      activeViewSystemName ?? undefined
    );

    const activeViewDisplayName = useMemo(() => {
      if (!view) return undefined;
      const lang = i18n.language;
      return view.name?.translations?.[lang] || view.name?.default || view.system_name;
    }, [view, i18n.language]);

    const mergedViewConfig = useMemo<AtlasViewConfig>(() => {
      if (!view?.view_config) return ATLAS_DEFAULT_VIEW_CONFIG;
      return mergeAtlasViewConfig(ATLAS_DEFAULT_VIEW_CONFIG, view.view_config as Partial<AtlasViewConfig>);
    }, [view]);

    // ── Column filters (EAINT-931): the view-wide ('*') scope of
    // `useLevelFilters`, composing `view.base_config.globalFilters` with the
    // personal overlay and session state — the same stack TreeMap's per-level
    // popover uses, narrowed by `LevelFilterPopoverOrganism`'s own
    // `focusProperty` to one column at a time (`atlas-header.tsx`).
    // A 403 on a column-filter write means `canEditActiveView` is stale — bump
    // the cache epoch so the next lookup re-fetches instead of leaving the
    // save gate fail-closed for the rest of the session (mirrors
    // `ContextShellOrganism.handleViewPermissionDenied`).
    const handleColumnFilterPermissionDenied = useCallback(() => {
      resetContextViewPermissionsCache();
    }, []);

    const handleColumnFilterWriteError = useCallback(() => {
      toastRef.current?.show({
        severity: 'error',
        summary: t('contextSwitcher.saveError'),
        life: 4000,
      });
    }, [t]);

    // The column currently open in a `LevelFilterPopoverOrganism` popover —
    // set by that popover's `onOpen` (via `AtlasViewContext.onColumnFilterOpen`)
    // so `useLevelFilters`'s `viewWideAspect` narrows `globalContextTiers` to
    // the one column actually being edited (EAINT-931 W6: SC7 context tier
    // visible for Atlas). `undefined` when no popover is open, which keeps
    // `globalContextTiers` empty rather than guessing a column.
    const [openColumnAspect, setOpenColumnAspect] = useState<string | undefined>(undefined);

    const {
      byLevel: columnFiltersByLevel,
      globalContextTiers,
      saveTargets: columnFilterSaveTargets,
      setLevelFilter: setColumnFilter,
      hasPendingSharedFilters: hasPendingColumnFilters,
      commitPendingSharedFilters: commitPendingColumnFilters,
      discardPendingSharedFilters: discardPendingColumnFilters,
    } = useLevelFilters({
      apiClients,
      contextSystemName: activeContextSystemName,
      contextDefinition: contextObj?.definition,
      viewSystemName: activeViewSystemName,
      view,
      canEditView: canEditActiveView,
      canEditViewLoading: canEditActiveViewLoading,
      onViewUpdated: setViewOverride,
      onWriteError: handleColumnFilterWriteError,
      onViewPermissionDenied: handleColumnFilterPermissionDenied,
      viewWideAspect: openColumnAspect,
    });

    const composedColumnFilters = columnFiltersByLevel[VIEW_WIDE_SCOPE];

    const handleColumnFilterSave = useCallback(
      (filters: UnifiedFilters, target: FilterSaveTarget) => setColumnFilter(VIEW_WIDE_SCOPE, filters, target),
      [setColumnFilter]
    );

    // ── Catalogs for the column popovers (EAINT-936) ──────────────────────────
    // Org-wide, not the "what can I create here" set `useAvailableTypes` above
    // answers — the level filter picker needs "what can I filter on", which a
    // sphere with no deployed approaches would otherwise answer with nothing
    // (same source TreeMap's own column popover uses).
    const { orgFilterSchemas: rawAllSchemas } = useAspectCatalog(apiClients);
    const allSchemas = rawAllSchemas as unknown as SchemaForFilters[];
    const { templates: arcTemplates } = useArcTemplates();

    const columnFilterPerspectiveSearchProps = useMemo(
      () =>
        sphereId
          ? {
              schemaSystemName: '',
              sphereId,
              organizationId,
            }
          : undefined,
      [sphereId, organizationId]
    );

    // "Share with view…" (EAINT-936 OQ-1: move, not copy) — buffers this
    // column's user-tier clauses into the pending shared-filter draft and
    // commits it in the same call via `overrides`, since `setColumnFilter`'s
    // `setState` isn't visible in this closure until the next render.
    const handleColumnFilterShare = useCallback(
      async (clauses: UnifiedFilters): Promise<void> => {
        setColumnFilter(VIEW_WIDE_SCOPE, clauses, 'view');
        // `attemptShare` (the popover) only distinguishes resolve from
        // reject — a `false` resolution (view save unavailable, no network
        // attempt) must reject too, or the popover would treat "did not
        // write" as "wrote" and clear its own local user-tier copy.
        const committed = await commitPendingColumnFilters({ [VIEW_WIDE_SCOPE]: clauses });
        if (!committed) throw new Error('Share with view is unavailable');
      },
      [setColumnFilter, commitPendingColumnFilters]
    );

    // A 'view'-target column filter edit or removal only buffers
    // (`useLevelFilters.setLevelFilter`) — it must NOT write until the
    // existing toolbar's Save is pressed. Atlas has no filter-bar surface of
    // its own (unlike `ContextShellOrganism` / TreeMap), so the buffer rides
    // the same toolbar's dirty/save/reset affordances every other unsaved
    // Atlas change would use, wired below. Auto-committing here previously
    // rewrote the shared view for every viewer the instant a row was removed
    // — no Save, no undo (ux-craft I4).
    const handleColumnFilterToolbarSave = useCallback(async () => {
      await commitPendingColumnFilters().catch(() => {
        /* surfaced via onWriteError/onViewPermissionDenied above */
      });
    }, [commitPendingColumnFilters]);

    // ── Switcher handlers ──────────────────────────────────────────────────────
    // Switching to a context renders that context's own atlas view (its node
    // molecules) when it has one; otherwise it falls back to the built-in atlas
    // defaults. Either way the previously-rendered view must not persist.
    const handleContextSelect = useCallback(
      (systemName: string) => {
        setActiveContextSystemName(systemName);
        setActiveViewSystemName(null);
        const schemaClient = apiClients?.schemaClient;
        if (!schemaClient) return;
        getContextViewsByContext(schemaClient, systemName)
          .then((views) => {
            const atlasView = views.find((v) => v.view_type === 'atlas');
            setActiveViewSystemName(atlasView?.system_name ?? null);
          })
          .catch(() => setActiveViewSystemName(null));
      },
      [apiClients, setActiveContextSystemName]
    );

    const handleViewSelect = useCallback(
      (selectedViewSystemName: string, _viewDisplayName: string, selectedContextSystemName: string) => {
        setActiveContextSystemName(selectedContextSystemName);
        setActiveViewSystemName(selectedViewSystemName);
      },
      [setActiveContextSystemName]
    );

    // Atlas has no live definition editing (unlike TreeMap/`ContextShellOrganism`)
    // — the only unsaved-changes source is the pending column-filter buffer
    // above, so Save-as-copy has nothing to copy.
    const noopAsync = useCallback(async () => {
      /* no copyable definition state on Atlas */
    }, []);

    // ── View editor overlay ──────────────────────────────────────────────────
    interface ViewEditorState {
      mode: 'new' | 'edit';
      viewSystemName?: string;
      contextSystemName: string;
      contextDisplayName: string;
    }
    const [viewEditorState, setViewEditorState] = useState<ViewEditorState | null>(null);
    const [switcherRefreshKey, setSwitcherRefreshKey] = useState(0);

    const handleNewView = useCallback(
      (ctxSn: string, ctxDisplayName: string) => {
        setViewEditorState({ mode: 'new', contextSystemName: ctxSn, contextDisplayName: ctxDisplayName });
      },
      []
    );

    const handleEditView = useCallback(
      (viewSn: string, ctxSn: string) => {
        const ctxDisplayName = ctxSn === activeContextSystemName ? activeContextDisplayName : ctxSn;
        setViewEditorState({ mode: 'edit', viewSystemName: viewSn, contextSystemName: ctxSn, contextDisplayName: ctxDisplayName });
      },
      [activeContextSystemName, activeContextDisplayName]
    );

    const handleViewSaved = useCallback(
      (savedViewSn: string) => {
        if (!viewEditorState) return;
        setActiveContextSystemName(viewEditorState.contextSystemName);
        setActiveViewSystemName(savedViewSn);
        setSwitcherRefreshKey((k) => k + 1);
        setViewEditorState(null);
      },
      [viewEditorState, setActiveContextSystemName]
    );

    const handleViewDeleted = useCallback(
      (deletedViewSn: string) => {
        if (deletedViewSn === activeViewSystemName) setActiveViewSystemName(null);
        setSwitcherRefreshKey((k) => k + 1);
        setViewEditorState(null);
      },
      [activeViewSystemName]
    );

    const atlasRef = useCallback((element: HTMLDivElement | null) => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      if (!element) return;

      const initialRect = element.getBoundingClientRect();
      if (initialRect.width > 0) {
        setContainerWidth(initialRect.width);
      }

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.borderBoxSize?.[0]?.inlineSize || entry.contentRect.width;
          if (width > 0) {
            setContainerWidth(width);
          }
        }
      });

      resizeObserver.observe(element);
      resizeObserverRef.current = resizeObserver;
    }, []);

    const responsiveBreakpoint = useMemo(() => getResponsiveBreakpoint(containerWidth), [containerWidth]);

    const contextValue = useMemo(
      () => ({
        contextSystemName: activeContextSystemName,
        viewConfig: mergedViewConfig,
        containerWidth,
        responsiveBreakpoint,
        composedColumnFilters,
        columnFilterSaveTargets,
        onColumnFilterSave: handleColumnFilterSave,
        activeViewDisplayName,
        globalContextTiers,
        onColumnFilterOpen: setOpenColumnAspect,
        arcTemplates,
        allSchemas,
        perspectiveSearchProps: columnFilterPerspectiveSearchProps,
        onShareWithView: handleColumnFilterShare,
        onViewPermissionDenied: handleColumnFilterPermissionDenied,
      }),
      [
        activeContextSystemName,
        mergedViewConfig,
        containerWidth,
        responsiveBreakpoint,
        composedColumnFilters,
        columnFilterSaveTargets,
        handleColumnFilterSave,
        activeViewDisplayName,
        globalContextTiers,
        arcTemplates,
        allSchemas,
        columnFilterPerspectiveSearchProps,
        handleColumnFilterShare,
        handleColumnFilterPermissionDenied,
      ]
    );

    if (!activeContextSystemName) {
      return (
        <div className="col-12 text-center">
          <ProgressSpinner
            className="custom-spinner"
            style={{ width: '40px', height: '40px', marginLeft: '10px', marginTop: '5px' }}
            strokeWidth="4"
          />
        </div>
      );
    }

    // The context/view selector lives inside the Atlas header (template slot),
    // not as a separate toolbar — it is passed down to AtlasTree → AtlasHeader.
    const contextSwitcher = (
      <ContextSwitcherToolbarMolecule
        activeContextSystemName={activeContextSystemName}
        activeContextDisplayName={activeContextDisplayName}
        defaultContextSystemName={contextSystemName}
        activeViewSystemName={activeViewSystemName}
        activeViewDisplayName={activeViewDisplayName}
        viewType="atlas"
        sphereId={sphereId ?? null}
        rootLabel={rootSphereName}
        organizationId={organizationId}
        canEditActiveContext={false}
        isDirty={hasPendingColumnFilters}
        dirtyKinds={hasPendingColumnFilters ? ['viewFilters'] : []}
        onSave={handleColumnFilterToolbarSave}
        onSaveAsCopy={noopAsync}
        onResetChanges={discardPendingColumnFilters}
        onContextSelect={handleContextSelect}
        onViewSelect={handleViewSelect}
        onNewView={handleNewView}
        onEditView={handleEditView}
        onEditContext={onEditContext}
        onNewContext={onNewContext}
        onManageContexts={onManageContexts}
        switcherRefreshKey={switcherRefreshKey}
      />
    );

    let treeComponent;
    if (apiClients && sphereId) {
      if (isMobile) {
        treeComponent = (
          <AtlasTreeMobile
            sphereId={sphereId}
            pageLimit={10}
            onChatBubbleClick={onChatBubbleClick}
            showToolbar={showToolbar}
          />
        );
      } else {
        treeComponent = (
          <AtlasTree
            sphereId={sphereId}
            pageLimit={pageLimit}
            onChatBubbleClick={onChatBubbleClick}
            containerWidth={containerWidth}
          />
        );
      }
    } else {
      treeComponent = (
        <div className="col-12 text-center">
          <ProgressSpinner
            className="custom-spinner"
            style={{ width: '40px', height: '40px', marginLeft: '10px', marginTop: '5px' }}
            strokeWidth="4"
          />
        </div>
      );
    }

    return (
      <AtlasViewContext.Provider value={contextValue}>
        <Toast ref={toastRef} />
        <div className="atlas-context-selector-container">
            {contextSwitcher}
        </div>
        <div
          ref={atlasRef}
          className={`atlas-component pt-1 breakpoint-${responsiveBreakpoint}`}
          style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}
          data-container-width={containerWidth}
        >
          {treeComponent}
          {viewEditorState && (
            <ContextViewEditorOverlayOrganism
              mode={viewEditorState.mode}
              contextSystemName={viewEditorState.contextSystemName}
              contextDisplayName={viewEditorState.contextDisplayName}
              viewSystemName={viewEditorState.viewSystemName}
              sphereId={sphereId}
              onSaved={handleViewSaved}
              onDeleted={handleViewDeleted}
              onClose={() => setViewEditorState(null)}
            />
          )}
        </div>
      </AtlasViewContext.Provider>
    );
  }
);

AtlasComponent.displayName = 'AtlasComponent';

export default AtlasComponent;
